import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { CommonFilter } from '@/src/utilities/common/headerFilterUtils'

type DisabledIngredientsState = {
  loading: boolean
  data: DisabledIngredientsByRestaurantDto[]
  count: number
  totalRestaurantsInNetwork: number
  error: SerializedError | null
}

type State = {
  disabledIngredients: DisabledIngredientsState

  /** Lazy-loaded per-restaurant details (admin list v2 popover) */
  ingredientsByRestaurantId: Record<number, DisabledIngredientDetailDto[]>
  ingredientsByRestaurantLoading: Record<number, boolean>
}

/** Menu product affected by a disabled ingredient (resolved via CerebroProduct posPLU mapping). */
export type AffectedProductDto = {
  posPlu: number
  productName: string

  /** Menu section name (e.g. "Burritos", "Drinks"). */
  sectionName?: string

  /** Menu template name (e.g. "Dine In", "Delivery"). */
  menuName?: string
}

// New API structure types
export type DisabledIngredientDetailDto = {
  id?: number // Optional for backward compatibility, use productCompanyId if not available
  productCompanyId: number
  productCompanyName: string

  /** Unique ingredient code for CerebroProduct join (e.g. "AUS-12345"). */
  productNameNumber?: string
  status: string
  isPermanentDisable: boolean
  daysElapsed: number | null // null for permanent
  createdAt: string
  reason: string
  reasonCategory?: string | null
  reasonCode?: string | null
  reasonOtherText?: string | null
  resolutionAt?: string | null
  notes?: string | null
  createdBy: number
  userName: string
  userRoleId: number
  userRoleName: string
  updatedBy?: number
  updatedAt?: string
  updatedByUserName?: string | null

  /** Menu products (by posPLU) affected by this disabled ingredient. */
  affectedProducts?: AffectedProductDto[]
}

/** Lightweight user-with-role tuple attached to each restaurant. */
export type RestaurantAssignedUserDto = {
  userId: number
  name: string
  roleId: number
  roleName: string
  roleHandle: string
}

export type DisabledIngredientsByRestaurantDto = {
  restaurantId: number
  restaurantName: string

  /** When true, show **Test** (takes precedence over live). */
  isTest?: boolean

  /** When true and not test, show **Live**; when false, **Inactive**. */
  isActive?: boolean

  state: string
  ownership: string
  timeZone?: string
  totalDisabledIngredients: number
  permanentDisabledCount: number
  temporaryDisabledCount: number
  averageDaysDisabled: number | null // null if no temporary
  maxDaysDisabled: number | null // null if no temporary
  ingredients: DisabledIngredientDetailDto[]

  /** Users assigned to this restaurant (coaches, managers, franchise). */
  assignedUsers?: RestaurantAssignedUserDto[]
}

// Legacy type kept for backward compatibility (deprecated)
export type RestoDisabledIngredients = {
  restaurantId: number
  restaurantName: string
  productCompanyId: number
  productCompanyName: string
  status: string
  createdBy: number
  userName: string
  userRoleId: number
  userRoleName: string
  createdAt: string
  daysElapsed: number
  state(state: string): CommonFilter[]
  ownership(ownership: string): CommonFilter[]
  reason: string
}

const initialState: State = {
  disabledIngredients: {
    loading: false,
    data: [],
    count: 0,
    totalRestaurantsInNetwork: 0,
    error: null
  },
  ingredientsByRestaurantId: {},
  ingredientsByRestaurantLoading: {}
}

type fetchDisabledIngredientsParams = {
  countryId: number
}

type DisabledIngredientsResponse = {
  restaurants: DisabledIngredientsByRestaurantDto[]
  totalRestaurantsInNetwork: number
}

export const fetchDisabledIngredients = createAsyncThunk<
  DisabledIngredientsResponse | null,
  fetchDisabledIngredientsParams,
  Redux
>('api/fetchDisabledIngredients', async (fetchDisabledIngredientsParams, { extra }) => {
  try {
    const response = await extra.httpClient(
      `resto365-cerebro-product-company-override/disabled-ingredients/${fetchDisabledIngredientsParams.countryId}`
    )

    const {
      data: { data: payload }
    } = response

    // Support both new wrapper format and legacy array format
    if (Array.isArray(payload)) {
      return { restaurants: payload, totalRestaurantsInNetwork: 0 } as DisabledIngredientsResponse
    }

    return payload as DisabledIngredientsResponse
  } catch (error) {
    throw new Error('Failed to fetch Disabled Ingredients')
  }
})

export const fetchDisabledIngredientsForRestaurant = createAsyncThunk<
  { restaurantId: number; ingredients: DisabledIngredientDetailDto[] },
  { countryId: number; restaurantId: number },
  Redux
>('api/fetchDisabledIngredientsForRestaurant', async ({ countryId, restaurantId }, { extra }) => {
  const response = await extra.httpClient(
    `resto365-cerebro-product-company-override/disabled-ingredients/${countryId}/restaurant/${restaurantId}`
  )

  const body = response.data?.data ?? response.data
  const ingredients = (body?.ingredients ?? []) as DisabledIngredientDetailDto[]

  return { restaurantId, ingredients }
})

type markAsPermanentParams = {
  id: number
  reason: string
}

export const markIngredientAsPermanent = createAsyncThunk<any, markAsPermanentParams, Redux>(
  'api/markIngredientAsPermanent',
  async ({ id, reason }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.put(`resto365-cerebro-product-company-override/mark-permanent/${id}`, {
        reason
      })

      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return rejectWithValue({ message: 'Ingredient not found', code: 'NOT_FOUND' })
      }
      if (error.response?.status === 403) {
        return rejectWithValue({ message: 'You do not have permission to perform this action', code: 'FORBIDDEN' })
      }
      if (error.response?.status === 401) {
        return rejectWithValue({ message: 'Unauthorized', code: 'UNAUTHORIZED' })
      }

      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to mark as permanently disabled',
        code: 'ERROR'
      })
    }
  }
)

const disabledIngredientsReducer = createSlice({
  name: 'disabledIngredients',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchDisabledIngredients.pending, state => {
        state.disabledIngredients.loading = true
        state.disabledIngredients.error = null
      })
      .addCase(fetchDisabledIngredients.fulfilled, (state, action) => {
        state.disabledIngredients.loading = false
        state.disabledIngredients.data = action.payload?.restaurants || []
        state.disabledIngredients.count = action.payload?.restaurants?.length || 0
        state.disabledIngredients.totalRestaurantsInNetwork = action.payload?.totalRestaurantsInNetwork || 0
      })
      .addCase(fetchDisabledIngredients.rejected, (state, action) => {
        state.disabledIngredients.loading = false
        state.disabledIngredients.error = action.error || null
      })
      .addCase(fetchDisabledIngredientsForRestaurant.pending, (state, action) => {
        state.ingredientsByRestaurantId ??= {}
        state.ingredientsByRestaurantLoading ??= {}
        const id = action.meta.arg.restaurantId
        state.ingredientsByRestaurantLoading[id] = true
      })
      .addCase(fetchDisabledIngredientsForRestaurant.fulfilled, (state, action) => {
        state.ingredientsByRestaurantId ??= {}
        state.ingredientsByRestaurantLoading ??= {}
        const { restaurantId, ingredients } = action.payload
        state.ingredientsByRestaurantId[restaurantId] = ingredients
        state.ingredientsByRestaurantLoading[restaurantId] = false
      })
      .addCase(fetchDisabledIngredientsForRestaurant.rejected, (state, action) => {
        const id = action.meta.arg?.restaurantId
        if (id == null) return
        state.ingredientsByRestaurantLoading ??= {}
        state.ingredientsByRestaurantLoading[id] = false
      })
  }
})

export default disabledIngredientsReducer.reducer
